import "./EditAddressRequest.scss";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { EditAddressColumns } from "../../SourceData";
import View from "./../../assets/show-user.svg";
import api from "../../utils/api";
import AddressReviewModal from "../../components/AddressReviewModal/AddressReviewModal";
import { setNotificationCount } from "../../redux/notificationsSlice";
import { usePermissions } from "./../../hooks/usePermissions";

const EditAddressRequest = () => {
  const { hasPermission } = usePermissions();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Listen to notification count changes from Redux (updated by middleware)
  // Fixed: Use the correct path to access counts from the notification slice
  const notificationCount = useSelector(
    (state) => state.notifications?.counts?.changeAddressRequests || 0
  );

  const fetchRequests = async () => {
    // Check view permission before fetching data
    if (!hasPermission(103)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      const response = await api.get(
        "/admin/subscriptions/changeAddress-requests",
        { params }
      );

      if (response.data.status === "success") {
        const data = response.data.data;
        const totalDocuments = response.data.totalDocuments || 0;

        setRequests(data);
        setTotalCount(totalDocuments);

        // Update Redux store with notification count
        // Fixed: Use the same key as in Home component and SidebarList
        dispatch(
          setNotificationCount({
            key: "changeAddressRequests",
            count: totalDocuments,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching address change requests:", error);
      toast.error(error.message || "حدث خطأ أثناء جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch requests when dependencies change
  useEffect(() => {
    fetchRequests();
  }, [page, pageSize]);

  // Effect to refetch when notification count changes (triggered by WebSocket middleware)
  useEffect(() => {
    // Only refetch if we're on this page and the count changed
    // This prevents unnecessary API calls when the middleware already updated the count
    if (notificationCount > 0 && totalCount !== notificationCount) {
      console.log(
        "Address change requests notification count changed via WebSocket, refreshing data..."
      );
      fetchRequests();
    }
  }, [notificationCount]);

  const handleOpenModal = (request) => {
    // Check approve/reject permission before opening modal
    if (!hasPermission(104)) {
      toast.error("ليس لديك صلاحية للموافقة أو الرفض على الطلبات");
      return;
    }

    setSelectedRequest(request);
    setModalOpen(true);
  };

  const handleCloseModal = (refreshData = false) => {
    setModalOpen(false);
    setSelectedRequest(null);
    if (refreshData) {
      fetchRequests(); // This will also update the notification count
    }
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "الإجراءات",
      width: 120,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // Check if user has permission to approve/reject requests
        const canApproveReject = hasPermission(104);

        return (
          <div className="cell-action">
            <img
              src={View}
              alt="عرض"
              style={{
                cursor: canApproveReject ? "pointer" : "not-allowed",
                opacity: canApproveReject ? 1 : 0.5,
              }}
              onClick={() => handleOpenModal(params.row)}
              title={
                canApproveReject
                  ? "عرض وتقييم الطلب"
                  : "ليس لديك صلاحية للموافقة أو الرفض"
              }
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          طلبات تعديل عنوان الاشتراك
          {notificationCount > 0 && (
            <span className="notification-badge">({notificationCount})</span>
          )}
        </p>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={EditAddressColumns.concat(actionColumn)}
          rows={requests.map((request, index) => ({
            ...request,
            // Generate unique id if not present
            id: request._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "طلب في الصفحة",
            },
          }}
        />
      </div>

      {modalOpen && hasPermission(104) && (
        <AddressReviewModal
          open={modalOpen}
          handleClose={handleCloseModal}
          requestData={selectedRequest}
        />
      )}
    </div>
  );
};

export default EditAddressRequest;
